import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Paper } from '@material-ui/core';

import { ActionBlockedForResponsivenessMessage } from './action-blocked-for-responsiveness-message';
import { loadApplicantAutomations } from '../../../applicant-automations/actions/load-applicant-automations';
import { removeApplicantAutomationOverrides } from '../../../applicant-automations/actions/remove-applicant-automation-overrides';
import { updateApplicantAutomation } from '../../../applicant-automations/actions/update-applicant-automation';
import { applicantAutomationsForOrgIdSelector } from '../../../applicant-automations/applicant-automation-selectors';
import { loadOrgJobs } from '../../../job/actions/load-org-jobs';
import { jobsForOrgIdSelector } from '../../../reducers/job-selectors';

import { UiDialog } from '../../../ui/ui-dialog';
import { UiGreenSwitch } from '../../../ui/ui-green-switch';
import { UiHideShowToggle } from '../../../ui/ui-hide-show-toggle';

import { ApplicantAutomationMessage } from './applicant-automation-message';

import { formatLocation } from '../../../util/formatters';
import { confirm } from '../../../widgets/confirmation-dialog';
import {
  automationNeededForHqcMetric,
  ConfirmRevertToDefaultText,
  getApplicationAutomationTiming,
} from '../../utils/applicant-automation-display-utils';

type InputProps = {
  orgId: number;
  orgName: string;
  jobOverrides: Array<ApplicantAutomation>;
  handleToggleDialog: () => void;
};

export const JobOverridesDialog = (props: InputProps) => {
  const { orgId, orgName, jobOverrides, handleToggleDialog } = props;

  const [showDetailsForId, setShowDetailsForId] = useState<number | null>(null);
  const [blockedActionType, setBlockedActionType] = useState<'turn off' | 'revert' | null>(null);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(loadOrgJobs(orgId));
    dispatch(loadApplicantAutomations({ orgId }));
  });

  const changeShowDetailsFor = (automationId: number) => () => {
    if (automationId === showDetailsForId) {
      setShowDetailsForId(null);
    } else {
      setShowDetailsForId(automationId);
    }
  };

  const jobs = useSelector((state: any) => jobsForOrgIdSelector(state, orgId));
  const applicantAutomations = useSelector((state: any) =>
    applicantAutomationsForOrgIdSelector(state, orgId),
  );

  if (!jobs) {
    return null;
  }

  const toggleActionBlocked = (action: 'turn off' | 'revert' | 'delete' | null) => {
    setBlockedActionType(action === 'delete' ? 'revert' : action);
  };

  const toggleAutomationOnOff = (automationToUpdate: ApplicantAutomation, job: Job) => () => {
    const action = 'turn off';
    const cannotTurnOffForHqcMetric = automationNeededForHqcMetric(
      automationToUpdate,
      applicantAutomations,
      orgId,
      [job],
      action,
    );
    if (cannotTurnOffForHqcMetric) {
      toggleActionBlocked(action);
      return;
    }

    dispatch(
      updateApplicantAutomation(automationToUpdate.applicantTriggeredCommunicationId, {
        isActive: !automationToUpdate.isActive,
      }),
    );
  };

  const handleRevertToDefault = (automationToRevert: ApplicantAutomation, job: Job) => async () => {
    const action = 'revert';
    const cannotDeleteForHqcMetric = automationNeededForHqcMetric(
      automationToRevert,
      applicantAutomations,
      orgId,
      [job],
      action,
    );
    if (cannotDeleteForHqcMetric) {
      toggleActionBlocked(action);
      return;
    }

    if (await confirm(ConfirmRevertToDefaultText)) {
      dispatch(
        removeApplicantAutomationOverrides(
          automationToRevert.applicantTriggeredCommunicationId,
          'deleteOneOverride',
        ),
      );
    }
  };

  if (!jobOverrides.length) {
    return (
      <UiDialog onClose={handleToggleDialog} size="sm" title="Custom Job Messages">
        <UiDialog.Content>
          <div className="typo-title u-width-100 u-margin-y-2x u-textAlign-center">
            No custom job messages to view for this automation
          </div>
        </UiDialog.Content>
      </UiDialog>
    );
  }

  return (
    <UiDialog onClose={handleToggleDialog} size="sm" title="Custom Job Messages">
      <UiDialog.Content>
        {jobOverrides.map((jobOverride) => {
          const jobForOverride = jobs.find((job) => job.job_id === jobOverride.jobId);
          return jobForOverride ? (
            <div
              className="u-margin-y-md"
              key={`job-override-${jobForOverride.job_id}-${jobOverride.applicantTriggeredCommunicationId}`}
            >
              <Paper className="u-margin-y u-padding u-width-100">
                <div className="u-display-flex u-justifyContent-spaceBetween">
                  <div className="u-textOverflow-ellipsis">
                    {jobForOverride.title}
                    {formatLocation(jobForOverride) ? ` (${formatLocation(jobForOverride)})` : ''}
                  </div>
                  <UiGreenSwitch
                    checked={!!jobOverride.isActive}
                    onChange={toggleAutomationOnOff(jobOverride, jobForOverride)}
                  />
                </div>
                {blockedActionType && (
                  <ActionBlockedForResponsivenessMessage
                    variant="warning"
                    job={jobForOverride}
                    onClose={toggleActionBlocked}
                    action={blockedActionType}
                  />
                )}
                <UiHideShowToggle
                  isOpen={showDetailsForId === jobOverride.applicantTriggeredCommunicationId}
                  title="Details"
                  onChange={changeShowDetailsFor(jobOverride.applicantTriggeredCommunicationId)}
                />
                {showDetailsForId === jobOverride.applicantTriggeredCommunicationId ? (
                  <div className="u-margin-top">
                    <div className="typo-subtitle">
                      {getApplicationAutomationTiming(jobOverride)}
                    </div>
                    <ApplicantAutomationMessage automation={jobOverride} orgName={orgName} />
                    <div
                      role="presentation"
                      className="typo-subtitle typo-link u-alignSelf-flexEnd u-margin-bottom-sm"
                      onClick={handleRevertToDefault(jobOverride, jobForOverride)}
                    >
                      Revert to Default
                    </div>
                  </div>
                ) : null}
              </Paper>
            </div>
          ) : null;
        })}
      </UiDialog.Content>
    </UiDialog>
  );
};
